import React, { useState, useEffect } from "react";
import { getRequest, patchRequest, postRequest } from "../../axiosRoutes/index";
import {
  Button,
  Modal,
  Box,
  Typography,
  OutlinedInput,
  FormControl,
  InputLabel,
  Grid,
  Hidden,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Snackbar,
  Checkbox,
  Alert,
} from "@mui/material";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import invoice from "../../images/invoice.png";
import DeleteIcon from "@mui/icons-material/Delete";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import AddIcon from "@mui/icons-material/Add";

const AddInvoice = ({ open, handleClose, invoiceId }) => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [invoiceData, setInvoiceData] = useState({
    title: "",
    invoiceNumber: "",
    clientId: "",
    clientName: "",
    projectsList: [],
    taskList: [],
    currency: "",
    date: "",
    status: "",
    work_type: "project",
    bankDetails: {_id: '', title: '', note: '', details: ''}
  });
  const [bankData, setBankData] = useState([])

  const fetchStatus = async () => {
    try {
      const response = await getRequest("filter/invoice");
      const statusArray = Object.keys(response).map((key) => ({
        label: key,
        value: response[key],
      }));
      setInvoiceStatus(statusArray);
    } catch (error) {
      console.error("Error fetching Filter:", error);
    }
  };

  const fetchBankDetails = async () => {
      const access = localStorage.getItem("access");
      try {
        const response = await getRequest("admin/bank/details", {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        });
        setBankData(response.data);
      } catch (error) {
        console.error("Error fetching Bank Details:", error);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchStatus();
    fetchBankDetails()
  }, []);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (invoiceId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const res = await getRequest(`invoice/${invoiceId}`, {
            headers: {
              Authorization: `Bearer ${access}`,
            },
          });
          const formatDate = new Date(res.date).toISOString().split("T")[0];
          setInvoiceData({
            title: res.title,
            invoiceNumber: res.invoiceNumber,
            clientId: res.clientId,
            clientName: res.clientName,
            projectsList: Array.isArray(res.projectsList)
              ? res.projectsList.map((item) => ({
                projectId: item.projectId || "",
                projectName: item.projectName || "",
                amount: item.amount || "",
                hours: item.hours || "",
              }))
              : [{name: '', hours: '', amount: ''}],
              taskList: res.taskList.length > 0 ? res.taskList.map(item => ({
                id: item.id || '1',
                name: item.name || '',
                hours: item.hours || '',
                amount: item.amount || ''
              })) : [{id: '1', name: '', hours: '', amount: ''}],
              work_type: res.work_type,
            currency: res.currency,
            date: formatDate,
            status: res.status,
            bankDetails: res.bankDetails
          });
          const filtered = projects.filter((proj) => proj.clientId === res.clientId);
          setFilteredProjects(filtered);
        } catch (error) {
          console.error("Error fetching Invoice:", error);
        } finally {
          setLoading(false);
        }
      } else {
        const firstTaskId = 1;
        setInvoiceData({
          title: "",
          invoiceNumber: "",
          clientId: "",
          clientName: "",
          projectsList: [],
          taskList: [{id: firstTaskId, name: '', hours: '', amount: ''}],
          currency: "",
          date: "",
          status: "",
          work_type: 'project',
          bankDetails: {_id: '', title: '', note: '', details: ''}
        });
      }
    };
    fetchInvoice();
  }, [invoiceId, projects]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    let dataToSend = { ...invoiceData };

  if(dataToSend.work_type === 'project'){
    dataToSend.taskList = []; 
  } else if (dataToSend.work_type === 'task'){
    dataToSend.projectsList = [];
  }
    setLoading(true);
    try {
      const access = localStorage.getItem("access");
      await patchRequest(
        `invoice/${invoiceId}`,
        {
          Authorization: `Bearer ${access}`,
        },
        dataToSend
      );
      showSnackbar("invoice Updated Successfully", "success");
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      const access = localStorage.getItem("access");
      try {
        const response = await getRequest("project", {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        });
        setProjects(response.data);
        const clientMap = new Map();
        response.data.forEach((proj) => {
          if (!clientMap.has(proj.clientId)) {
            clientMap.set(proj.clientId, {
              clientId: proj.clientId,
              clientName: proj.clientName,
            });
          }
        });
        const clientList = Array.from(clientMap.values());
        setClients(clientList);
      } catch (error) {
        console.error("Error fetching Projects:", error);
      }
    };
    fetchProjects();
  }, []);

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    const filtered = projects.filter((proj) => proj.clientId === clientId);
    setFilteredProjects(filtered);
    const client = clients.find((c) => c.clientId === clientId);
    setInvoiceData((prevData) => ({
      ...prevData,
      clientId,
      clientName: client?.clientName || "",
      projectsList: filtered.map((proj) => ({
        projectId: proj._id,
        projectName: proj.title,
        amount: "",
      })),
    }));
  };

  useEffect(()=>{
    const filtered = projects.filter((proj) => proj.clientId === invoiceData.clientId);
    setInvoiceData(prevData =>({
      ...prevData,
      projectsList: filtered.map((proj) => ({
        projectId: proj._id,
        projectName: proj.title,
        amount: "",
      })),
    }))
  }, [invoiceData.work_type])

  const handleTaskChange = (e, index) => {
      const { name, value } = e.target;

    setInvoiceData(prev => {
      const updated = [...prev.taskList];
      updated[index][name] = value;
      return { ...prev, taskList: updated };
    });
    }
    

  const handleProjectChange = (e, index) => {
    const selectedProjectId = e.target.value;
    const selectedProject = filteredProjects.find(
      (proj) => proj._id === selectedProjectId
    );

    if (!selectedProject) {
      console.warn(
        `Project with ID ${selectedProjectId} not found in filteredProjects.`
      );
      return;
    }

    const updatedProjectsList = [...invoiceData.projectsList];
    updatedProjectsList[index] = {
      ...updatedProjectsList[index],
      projectId: selectedProject._id,
      projectName: selectedProject.title,
    };

    setInvoiceData((prevData) => ({
      ...prevData,
      projectsList: updatedProjectsList,
    }));
  };

  const handleAmountChange = (e, index) => {
    const { value } = e.target;

    const updatedProjectsList = [...invoiceData.projectsList];
    updatedProjectsList[index] = {
      ...updatedProjectsList[index],
      amount: value,
    };

    setInvoiceData((prevData) => ({
      ...prevData,
      projectsList: updatedProjectsList,
    }));
  };

  const handleHoursChange = (e, index) => {
    const { value } = e.target;

    const updatedProjectsList = [...invoiceData.projectsList];
    updatedProjectsList[index] = {
      ...updatedProjectsList[index],
      hours: value,
    };

    setInvoiceData((prevData) => ({
      ...prevData,
      projectsList: updatedProjectsList,
    }));
  };

  const handleBankDetailsChange = (e) => {
    const {value} = e.target
    const selectedBank = bankData.find((bank) => bank._id === value);
    setInvoiceData(prev => ({...prev, bankDetails: {_id: selectedBank._id, title: selectedBank.title, details: selectedBank.details}}))
  }

  const addTask = () => {
    const maxId = invoiceData.taskList.length
      ? Math.max(...invoiceData.taskList.map(t => Number(t.id)))
      : 0;

    const nextId = maxId + 1;
    setInvoiceData(prev => ({
      ...prev,
      taskList: [...prev.taskList, {id: nextId, name: '', hours: ''}]
    }))
  }

  const handleRemoveProject = (index) => {
    const updatedProjectsList = invoiceData.projectsList.filter(
      (_, i) => i !== index
    );
    setInvoiceData((prevData) => ({
      ...prevData,
      projectsList: updatedProjectsList,
    }));
  };

  const handleRemoveTask = (id) => {
    const updatedTaskList = invoiceData.taskList.filter(task => task.id !== id)
    setInvoiceData(prevData => ({
      ...prevData, 
      taskList: updatedTaskList
    }))
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData({ ...invoiceData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let dataToSend = { ...invoiceData };

  if(dataToSend.work_type === 'project'){
    dataToSend.taskList = []; 
  } else if (dataToSend.work_type === 'task'){
    dataToSend.projectsList = [];
  }
    setLoading(true);
    try {
      const access = localStorage.getItem("access");
      await postRequest(
        "invoice",
        {
          Authorization: `Bearer ${access}`,
        },
        dataToSend
      );
      showSnackbar("Invoice Added Successfully", "success");
      setInvoiceData({
        title: "",
        invoiceNumber: "",
        clientId: "",
        clientName: "",
        work_type: "",
        projectsList: [],
        currency: "",
        date: "",
        status: "",
      });
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '80%', md: '50%' },
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "black",
            color: "#FEFEFE",
            border: "1px solid #A9A9A9",
            p: 2,
            "& .MuiOutlinedInput-root": {
              color: "#FEFEFE",
              "& fieldset": {
                borderColor: "#FEFEFE",
              },
              "&:hover fieldset": {
                borderColor: "#FEFEFE",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FEFEFE",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#FEFEFE",
            },
            "& .MuiIconButton-root": {
              color: "#FEFEFE",
            },
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            component="h1"
            sx={{ mb: 2 }}
          >
            {invoiceId !== null ? "Update Invoice Record" : "Add Invoice Record"}
          </Typography>
          <Grid container spacing={2}>
            <Hidden mdDown>
              <Grid item md={4} justifyContent="center" alignContent="center">
                <img
                  src={invoice}
                  alt="Invoice"
                  style={{ width: "100%", height: "80%", borderRadius: "8px" }}
                />
              </Grid>
            </Hidden>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel htmlFor="outlined-adornment-title">
                      Invoice Title
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-title"
                      type="text"
                      name="title"
                      label="Invoice Title"
                      value={invoiceData.title}
                      onChange={handleChange}
                      sx={{ backgroundColor: "transparent" }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel htmlFor="outlined-adornment-invoiceNumber">
                      Invoice Number
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-invoiceNumber"
                      type="text"
                      name="invoiceNumber"
                      label="Invoice Number"
                      readOnly={invoiceId === null}
                      value={invoiceData.invoiceNumber}
                      onChange={handleChange}
                      sx={{ backgroundColor: "transparent" }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="client-select-label">Client Name</InputLabel>
                    <Select
                      labelId="client-select-label"
                      value={invoiceData.clientId}
                      onChange={handleClientChange}
                      label="Client Name"
                    >
                      {!clients ? <MenuItem disabled={true}>No Data yet</MenuItem> : clients.map((client) => (
                        <MenuItem key={client.clientId} value={client.clientId}>
                          {client.clientName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel
                      htmlFor="outlined-adornment-date"
                      shrink
                      sx={{
                        backgroundColor: "#000",
                        paddingX: "4px",
                        marginLeft: "-4px",
                      }}
                    >
                      Date
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-date"
                      type="date"
                      name="date"
                      label="Date"
                      value={invoiceData.date}
                      onChange={handleChange}
                      sx={{ backgroundColor: "transparent" }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormControl>
                    <FormLabel id="demo-radio-buttons-group-label" sx={{ color: 'white !important' }}>Work Type</FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue="project"
                      name="work_type"
                      value={invoiceData.work_type}
                      sx={{ display: 'flex', flexDirection: 'row' }}
                      onChange={handleChange}
                    >
                      <FormControlLabel value="project" control={<Radio sx={{
                        color: "white",
                        '&.Mui-checked': { color: "white" },
                      }} />} label="Project Based" />
                      <FormControlLabel value="task" control={<Radio sx={{
                        color: "white",
                        '&.Mui-checked': { color: "white" },
                      }} />} label="Task Based" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {invoiceData.work_type !== 'project' && <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            alignItems: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={addTask}
                                            // disabled={isReadOnly}
                                            sx={{
                                                bgcolor: "#fff",
                                                color: "#000",
                                                "&:hover": { bgcolor: "#e1e1e1" },
                                            }}
                                        >
                                            Add Task
                                        </Button>
                                    </Box>
                                </Grid>}
                <Grid item xs={12} sx={{display: invoiceData.work_type !== "project" ? 'none' : 'block'}}>
                  {invoiceData.work_type === "project" && invoiceData.projectsList.map((project, index) => (
                    <Grid container spacing={2} key={index} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                          <InputLabel id={`project-select-label-${index}`}>
                            Project Name
                          </InputLabel>
                          <Select
                            labelId={`project-select-label-${index}`}
                            value={project.projectId}
                            onChange={(e) => handleProjectChange(e, index)}
                            label="Project Name"
                          >
                            {filteredProjects.map((proj) => (
                              <MenuItem key={proj._id} value={proj._id}>
                                {proj.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                          <InputLabel
                            htmlFor={`outlined-adornment-amount-${index}`}
                          >
                            Hours
                          </InputLabel>
                          <OutlinedInput
                            id={`outlined-adornment-amount-${index}`}
                            type="text"
                            name="hours"
                            label="Hours"
                            value={project.hours}
                            onChange={(e) => handleHoursChange(e, index)}
                            sx={{ backgroundColor: "transparent" }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                          <InputLabel
                            htmlFor={`outlined-adornment-amount-${index}`}
                          >
                            Amount
                          </InputLabel>
                          <OutlinedInput
                            id={`outlined-adornment-amount-${index}`}
                            type="text"
                            name="amount"
                            label="Amount"
                            value={project.amount}
                            onChange={(e) => handleAmountChange(e, index)}
                            sx={{ backgroundColor: "transparent" }}
                          />
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleRemoveProject(index)}
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </FormControl>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={12}>
                  {invoiceData.work_type === "task" && invoiceData.taskList.map((task, index)=>(
                    <Grid container spacing={2} alignItems="center" key={task.id} sx={{paddingBottom: '20px'}}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Task Name</InputLabel>
                          <OutlinedInput
                            name="name"
                            value={task.name}
                            onChange={(e) => handleTaskChange(e, index)}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Hours</InputLabel>
                          <OutlinedInput
                            name="hours"
                            value={task.hours}
                            onChange={(e) => handleTaskChange(e, index)}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Amount</InputLabel>
                          <OutlinedInput
                            name="amount"
                            value={task.amount}
                            onChange={(e) => handleTaskChange(e, index)}
                          />
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleRemoveTask(task.id)}
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </FormControl>
                      </Grid>

                    </Grid>))}
                </Grid>
                <Grid item xs={12} md={6} mt={-2}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="currency-label">Currency</InputLabel>
                    <Select
                      labelId="currency-label"
                      id="outlined-adornment-currency"
                      name="currency"
                      label="Currency"
                      value={invoiceData.currency}
                      onChange={handleChange}
                      sx={{ backgroundColor: "transparent" }}
                    >
                      <MenuItem value="USD">Dollar</MenuItem>
                      <MenuItem value="PKR">PKR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} mt={-2}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel htmlFor="outlined-adornment-status">
                      Payment Status
                    </InputLabel>
                    <Select
                      labelId="outlined-adornment-status-label"
                      id="outlined-adornment-status"
                      name="status"
                      label="Payment Status"
                      value={invoiceData.status}
                      onChange={handleChange}
                      sx={{ backgroundColor: "transparent" }}
                    >
                      {invoiceStatus.map((statusOpt) => (
                        <MenuItem key={statusOpt.value} value={statusOpt.value}>
                          {statusOpt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="client-select-label">Bank Details</InputLabel>
                    <Select
                      labelId="client-select-label"
                      name="bankDetails"
                      value={invoiceData.bankDetails?._id}
                      onChange={handleBankDetailsChange}
                      label="Bank Details"
                    >
                      {bankData.map((bank) => (
                        <MenuItem key={bank._id} value={bank._id}>
                          Title: {bank.title} {bank.details}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!invoiceData.bankDetails?.note}
                        onChange={(e) =>
                          setInvoiceData((prev) => ({
                            ...prev,
                            bankDetails: {...prev.bankDetails, note: e.target.checked
                              ? 'Note: Please add the Beneficiary name as "Muhammad Hasan Alvi" not "Burnt Brains"'
                              : "",}
                          }))
                        }
                        sx={{
                          color: "#FEFEFE",
                          "&.Mui-checked": {
                            color: "#FF5722",
                          },
                        }}
                      />
                    }
                    label={
                      <span style={{ color: "#FEFEFE" }}>
                        Note: Please add the Beneficiary name as 'Muhammad Hasan Alvi' not 'Burnt Brains'
                      </span>
                    }
                  />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="space-around">
                  {invoiceId !== null ? (
                    <Grid item xs={5}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleUpdate}
                        sx={{
                          bgcolor: "#E5622D",
                          "&:hover": { bgcolor: "#D0521B" },
                        }}
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                        ) : (
                          "Update"
                        )}
                      </Button>
                    </Grid>
                  ) : (
                    <Grid item xs={5}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit}
                        sx={{
                          bgcolor: "#E5622D",
                          "&:hover": { bgcolor: "#D0521B" },
                        }}
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                        ) : (
                          "Add"
                        )}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={5}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleClose}
                      disabled={loading}
                      sx={{
                        bgcolor: "#A9A9A9",
                        "&:hover": { bgcolor: "#E1E1E1" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddInvoice;
